import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import classnames from 'classnames/bind';
import { shuffle, take, isEmpty } from 'lodash';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { DISABLED_CHAT_STATE } from '../../consts/chatState';
import { ChatSuggestions, suggestions } from './ChatSuggestions';
import styles from './chatHistory.module.scss';

const classNameBuilder = classnames.bind(styles);

export const ChatHistory = ({ onSuggestionSubmit, disabled = false }) => {
    const intl = useIntl();
    const chatEndRef = useRef();
    const { value: chatHistory } = useFormFieldValue({ field: 'chatHistory' });
    const [visibleSuggestions, setVisibleSuggestions] = useState(() => take(shuffle(suggestions), 2));
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [usedSuggestionIds, setUsedSuggestionIds] = useState(new Set());

    const unusedSuggestions = useMemo(() => {
        return suggestions.filter(s => !usedSuggestionIds.has(s.id));
    }, [usedSuggestionIds]);

    useEffect(() => {
        if (isEmpty(unusedSuggestions)) {
            setShowSuggestions(false);
            setVisibleSuggestions([]);
        } else {
            const newSuggestions =
                unusedSuggestions.length === 1 ? [unusedSuggestions[0]] : take(shuffle(unusedSuggestions), 2);
            setVisibleSuggestions(newSuggestions);
        }
    }, [unusedSuggestions]);

    // Auto-scroll to bottom when content changes
    useLayoutEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory, showSuggestions, visibleSuggestions]);

    const handleSuggestionClick = useCallback(
        async suggestion => {
            const translatedText = intl.formatMessage({ id: suggestion.id, defaultMessage: suggestion.defaultText });
            setUsedSuggestionIds(prev => new Set([...prev, suggestion.id]));
            await onSuggestionSubmit(translatedText);
        },
        [onSuggestionSubmit, intl]
    );

    const toggleSuggestions = useCallback(() => {
        if (isEmpty(unusedSuggestions)) {
            return; // Don't allow showing suggestions if none are left
        }
        setShowSuggestions(prev => !prev);
        if (!showSuggestions && !isEmpty(unusedSuggestions)) {
            const newSuggestions =
                unusedSuggestions.length === 1 ? [unusedSuggestions[0]] : take(shuffle(unusedSuggestions), 2);
            setVisibleSuggestions(newSuggestions);
        }
    }, [unusedSuggestions, showSuggestions]);

    const messages = disabled ? DISABLED_CHAT_STATE : chatHistory;
    const shouldShowSuggestions = !disabled && !isEmpty(visibleSuggestions);

    return (
        <div className={styles['chat-history']}>
            {messages.map((messageData, index) => {
                const messageKey = `${messageData.sender}-${index}`;
                return <Message key={messageKey} messageData={messageData} index={index} />;
            })}
            {shouldShowSuggestions && (
                <ChatSuggestions
                    visibleSuggestions={visibleSuggestions}
                    showSuggestions={showSuggestions}
                    onSuggestionClick={handleSuggestionClick}
                    onToggleSuggestions={toggleSuggestions}
                />
            )}
            <div ref={chatEndRef} />
        </div>
    );
};

const Message = ({ messageData, index }) => {
    const { sender, ...contentProps } = messageData;

    const messageContent = innerMessage(contentProps);

    return (
        <div key={`${sender}-${index}`} className={classNameBuilder('message-container', sender)}>
            <p className={classNameBuilder('message', sender)}>{messageContent}</p>
        </div>
    );
};

const innerMessage = ({ message, messageId, defaultText, text }) => {
    if (message) {
        return message;
    }

    if (messageId) {
        return <FormattedMessage id={messageId} defaultMessage={defaultText} />;
    }

    return text;
};
